import { gql } from '@apollo/client';
import { Checkbox, Col, Form, Row, Select } from 'antd';
import { availableAddons } from 'shared/constants/license';
import { useQuery } from 'hooks/apollo';
import React, { useEffect } from 'react';
import { visitLimitOptions } from 'shared/constants/visitLimits';
import { DiscountType, LicenseInterval } from 'shared/prisma/types';
import { dayjs } from 'shared/utils/date';

import { SEK } from 'shared/constants/currencies';
import { getValidLicenseInterval } from 'shared/utils/license';
import LicenseForm from './LicenseForm';

const LICENSES = gql`
  query ActiveLicenses {
    activeLicenses {
      id
      title
      status
      level
      settings(keys: ["yearlyPrices", "monthlyPrices", "dedicatedAnalyst", "unlimitedHeatmaps"])
    }
  }
`;

const LegacyLicenseForm = ({ form, dataSource, onSubmit, onChange }) => {
  const { data, loading } = useQuery(LICENSES);
  const licenseId = Form.useWatch('licenseId', form);
  const visitLimit = Form.useWatch('visitLimit', form);
  const interval = Form.useWatch('interval', form);
  const license = data?.activeLicenses.find(l => l.id === licenseId);

  const getValues = (values) => ({
    ...values,
    settings: [
      { key: 'matomoMonthlyVisitLimit', value: values.visitLimit },
      { key: 'dedicatedAnalyst', value: values.addons.includes('dedicatedAnalyst') },
      { key: 'unlimitedHeatmaps', value: values.addons.includes('unlimitedHeatmaps') },
    ],
    license: data?.activeLicenses.find(l => l.id === values.licenseId),
  });

  const onFinish = async (values) => {
    onSubmit(getValues(values));
  };

  const onValuesChange = (changedValues, allValues) => {
    onChange(getValues(allValues));
  };

  const initialValues = {
    licenseId: data?.activeLicenses.find(l => l.title === dataSource.activeLicense?.license?.title)?.id || data?.activeLicenses[0].id,
    interval: getValidLicenseInterval({
      activeLicense: dataSource.activeLicense,
      startDate: dayjs(),
      interval: dataSource.activeLicense?.interval || LicenseInterval.Yearly,
    }),
    visitLimit: dataSource.activeLicense?.settings.matomoMonthlyVisitLimit || visitLimitOptions[0].value,
    addons: [],
    startDate: dayjs(),
    discountType: DiscountType.Amount,
    currency: SEK,
  };

  if (dataSource.activeLicense?.settings.dedicatedAnalyst) {
    initialValues.addons.push('dedicatedAnalyst');
  }

  if (dataSource.activeLicense?.settings.unlimitedHeatmaps) {
    initialValues.addons.push('unlimitedHeatmaps');
  }

  const addonIncluded = (addon) => license?.settings[addon];

  const addonAvailable = (addon) => {
    const prices = license?.settings?.[interval === LicenseInterval.Yearly ? 'yearlyPrices' : 'monthlyPrices']?.find(p => p.matomoMonthlyVisitLimit === visitLimit);

    if (prices?.[addon] != null) {
      return true;
    }

    return false;
  };

  const addonLabel = (addon) => {
    let { label } = addon;

    if (addonIncluded(addon.value)) {
      label += ' (included)';
    } else if (!addonAvailable(addon.value)) {
      label += ' (not available)';
    }

    return label;
  };

  useEffect(() => {
    form.resetFields();
    onValuesChange({}, initialValues);
  }, [dataSource, data]);

  useEffect(() => {
    let addons = form.getFieldValue('addons');

    for (const { value: addon } of availableAddons) {
      if (addonIncluded(addon)) {
        if (!addons.includes(addon)) {
          addons.push(addon);
        }
      } else if (!addonAvailable(addon)) {
        addons = addons.filter(a => a !== addon);
      }
    }

    form.setFieldsValue({ addons });
    onValuesChange({}, form.getFieldsValue());
  }, [visitLimit, interval, license]);

  return (
    <Form form={form} onFinish={onFinish} onValuesChange={onValuesChange} layout="vertical" initialValues={initialValues}>
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Form.Item label="License" name="licenseId" rules={[{ required: true }]}>
            <Select options={data?.activeLicenses.map(l => ({ label: l.title, value: l.id }))} loading={loading} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="visitLimit" label="Visits/month">
            <Select options={visitLimitOptions} />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item name="addons" label="Addons">
        <Checkbox.Group options={availableAddons.map(a => ({ value: a.value, label: addonLabel(a), disabled: addonIncluded(a.value) || !addonAvailable(a.value) }))} />
      </Form.Item>
      <LicenseForm dataSource={dataSource} />
    </Form>
  );
};

export default LegacyLicenseForm;
